/*
 * Recently viewed products, kept in this browser only (localStorage).
 *
 * Product pages record a visit through snippets/custom-recently-viewed.liquid:
 * <custom-recently-viewed-record data-product-id data-handle>. Each product
 * keeps its visit times for 30 days; views of the same product less than 5
 * minutes apart count as one visit, so reloads and variant clicks do not
 * inflate the count.
 *
 * window.customRecentlyViewed.list() returns the products still in the window,
 * most visited first, then most recent: [{ id, handle, count, last }].
 * remove(id) drops one product; cards() fetches the viewed products' cards.
 * Used by the cart shelf (custom-cart-suggestions.js) and the home page list
 * and home page lists (<custom-recently-viewed-grid>, below).
 *
 * Recording and reading both need preferences consent (Shopify's Customer
 * Privacy API, which the consent banner sets). Without it nothing is recorded,
 * list() returns nothing and any stored history is cleared.
 */
if (!window.customRecentlyViewed) {
  const KEY = 'lts-recently-viewed';
  const WINDOW = 30 * 24 * 60 * 60 * 1000;
  const SAME_VISIT = 5 * 60 * 1000;
  const MAX_PRODUCTS = 40;
  const MAX_VISITS = 30;

  const read = () => {
    try {
      const data = JSON.parse(localStorage.getItem(KEY));
      return data && typeof data === 'object' && data.products ? data : { products: {} };
    } catch (error) {
      return { products: {} };
    }
  };

  const write = (data) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch (error) {
      // Private mode or full storage: the history is a convenience only.
    }
  };

  const clear = () => {
    try {
      localStorage.removeItem(KEY);
    } catch (error) {
      // Nothing stored or storage blocked.
    }
  };

  // Drops visits older than 30 days, products left without visits, and the
  // least recently seen products beyond MAX_PRODUCTS.
  const prune = (data, now) => {
    const entries = Object.entries(data.products)
      .map(([id, entry]) => {
        const visits = (Array.isArray(entry.v) ? entry.v : []).filter((time) => now - time < WINDOW);
        return [id, { h: entry.h, v: visits.slice(-MAX_VISITS) }];
      })
      .filter(([, entry]) => entry.h && entry.v.length)
      .sort((a, b) => b[1].v[b[1].v.length - 1] - a[1].v[a[1].v.length - 1])
      .slice(0, MAX_PRODUCTS);
    return { products: Object.fromEntries(entries) };
  };

  // Resolves to true when preferences processing is allowed.
  let consentPromise;
  const consent = () => {
    if (consentPromise) return consentPromise;
    consentPromise = new Promise((resolve) => {
      const check = () => {
        const privacy = window.Shopify?.customerPrivacy;
        resolve(Boolean(privacy?.preferencesProcessingAllowed?.()));
      };
      if (window.Shopify?.customerPrivacy) {
        check();
      } else if (typeof window.Shopify?.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) =>
          error ? resolve(false) : check()
        );
      } else {
        resolve(false);
      }
    });
    return consentPromise;
  };

  const record = (id, handle) =>
    consent().then((allowed) => {
      if (!allowed) {
        clear();
        return;
      }
      const now = Date.now();
      const data = prune(read(), now);
      const entry = data.products[id] || { h: handle, v: [] };
      entry.h = handle;
      const last = entry.v[entry.v.length - 1];
      if (!last || now - last >= SAME_VISIT) entry.v.push(now);
      data.products[id] = entry;
      write(prune(data, now));
    });

  const list = () =>
    consent().then((allowed) => {
      if (!allowed) {
        clear();
        return [];
      }
      const data = prune(read(), Date.now());
      return Object.entries(data.products)
        .map(([id, entry]) => ({ id, handle: entry.h, count: entry.v.length, last: entry.v[entry.v.length - 1] }))
        .sort((a, b) => b.count - a.count || b.last - a.last);
    });

  const remove = (id) => {
    const data = read();
    delete data.products[id];
    write(prune(data, Date.now()));
  };

  // Cards of the viewed products, most visited first: <li data-product-id
  // data-price data-available> grid items from the custom-card-fragment section
  // rendered on each product URL, so price and availability are current and in
  // the visitor's currency. Sold-out products and ids in `exclude` (",1,2,")
  // are left out. Fetches are kept for the page's lifetime; each call returns
  // fresh copies.
  const FETCH_MAX = 16;
  const fetched = new Map();

  const fetchCard = (root, handle) => {
    const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=custom-card-fragment`;
    if (!fetched.has(url)) {
      fetched.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('li[data-product-id]'))
          .catch(() => null)
      );
    }
    return fetched.get(url).then((item) => (item ? item.cloneNode(true) : null));
  };

  const cards = ({ root = '/', exclude = ',' } = {}) =>
    list()
      .then((viewed) =>
        Promise.all(
          viewed
            .filter((product) => !exclude.includes(`,${product.id},`))
            .slice(0, FETCH_MAX)
            .map((product) => fetchCard(root || '/', product.handle))
        )
      )
      .then((items) => {
        const seen = new Set();
        return items.filter((item) => {
          if (!item || item.dataset.available !== 'true') return false;
          const id = item.dataset.productId;
          if (exclude.includes(`,${id},`) || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });

  // Puts the (x) from a <template> holding the translated button at the
  // card's top-right corner (styles in custom-recently-viewed.css).
  const withRemove = (item, template) => {
    if (!template) return item;
    const button = template.content.firstElementChild.cloneNode(true);
    button.dataset.recentRemove = item.dataset.productId;
    button.setAttribute('aria-describedby', `title-recent-${item.dataset.productId}`);
    item.prepend(button);
    return item;
  };

  window.customRecentlyViewed = { record, list, remove, cards, withRemove };

  // (x): forget the product, drop its card and let the list that held it
  // follow up (recentRemoved: refill or re-render).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-recent-remove]');
    if (!button) return;
    remove(button.dataset.recentRemove);
    const host = button.closest('[data-recent-host]');
    const list = button.closest('ul');
    button.closest('li')?.remove();
    list?.querySelector('[data-recent-remove]')?.focus();
    host?.recentRemoved?.();
  });

  // "Clear list" beside the heading: forget every product.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-recent-clear]');
    if (!button) return;
    clear();
    button.closest('[data-recent-host]')?.recentRemoved?.();
  });

  // A recently viewed list (snippets/custom-recently-viewed-list.liquid), on
  // the home page and first on the cart page: hidden until the visitor has
  // viewed at least one product that is still available.
  //   data-limit     how many cards
  //   data-exclude   ",1,2," product ids to leave out (the cart's items)
  //   data-gap       below free shipping, the remaining amount in cents: the
  //                  viewed products that reach it lead the cart shelf's gap
  //                  list instead (custom-cart-suggestions.js), so not here
  //   data-keep-add  keep the cards' Add buttons (they work on the cart page only)
  class CustomRecentlyViewedGrid extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      const section = this.closest('.recently-viewed');
      const list = this.querySelector('ul');
      const template = this.querySelector('.recently-viewed__remove-template');
      const limit = Number(this.dataset.limit) || 4;
      const gap = Number(this.dataset.gap) || 0;
      cards({ root: this.dataset.root, exclude: this.dataset.exclude || ',' }).then((items) => {
        const shown = items
          .filter((item) => !(gap > 0 && Number(item.dataset.price) >= gap))
          .slice(0, limit)
          .map((item) => {
            if (!this.hasAttribute('data-keep-add')) item.querySelector('.card-add')?.remove();
            return withRemove(item, template);
          });
        list.replaceChildren(...shown);
        if (section) section.hidden = shown.length === 0;
      });
    }

    // Refill from the rest of the history after an (x) or Clear list.
    recentRemoved() {
      this.render();
    }
  }
  customElements.define('custom-recently-viewed-grid', CustomRecentlyViewedGrid);

  // Visitors who withdraw consent lose the history on their next page view.
  document.addEventListener('visitorConsentCollected', (event) => {
    if (event.detail && event.detail.preferencesAllowed === false) clear();
  });

  class CustomRecentlyViewedRecord extends HTMLElement {
    connectedCallback() {
      if (this.dataset.productId && this.dataset.handle) record(this.dataset.productId, this.dataset.handle);
    }
  }
  customElements.define('custom-recently-viewed-record', CustomRecentlyViewedRecord);
}
